import * as React from 'react';

import type { ChatProfile, ThreadFilters } from '@/api/chat-client';
import { useThreadsQuery } from '@/api/use-chat';
import type { ChatSocketLike } from '@/api/chat-realtime';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ChatThreadList } from '@/routes/communication/chat-thread-list';
import { ChatThreadPanel } from '@/routes/communication/chat-thread-panel';

// =============================================================================
// chat-workspace.tsx — Surface scindée du chat (R15-COM-02), PARTAGÉE admin/fournisseur.
// =============================================================================
// Vue en deux volets : liste des fils (gauche) + fil chronologique + composition (droite). Le MÊME
// composant sert les deux surfaces miroir (DEC-31), paramétré par `profile`. Les CONTRÔLES de filtre
// (recherche SIRET `q`, campagne, non-lus) ne sont montrés QUE pour l'admin (`showAdminControls`) : le
// fournisseur ne voit que ses fils (un par campagne), sans recherche SIRET.
//
// ÉTAT DE VUE porté par l'appelant (URL, RULE_REF_006) : filtres + fil ouvert. Les défauts (aucun filtre)
// ne sont pas imposés ici. WCAG 2.1 AA : régions titrées, contrôles avec <label>, aucune couleur seule.

export interface ChatWorkspaceProps {
  readonly profile: ChatProfile;
  readonly title: string;
  readonly description: string;
  readonly filters: ThreadFilters;
  readonly onFiltersChange: (filters: ThreadFilters) => void;
  readonly selectedThreadId: string | null;
  readonly onSelectThread: (threadId: string | null) => void;
  /** Affiche la barre de filtres admin (recherche SIRET + campagne). Le non-lus reste commun. */
  readonly showAdminControls: boolean;
  /** Fabrique de socket injectable (tests) — transmise au panneau/hook temps réel. */
  readonly socketFactory?: () => ChatSocketLike;
}

export function ChatWorkspace({
  profile,
  title,
  description,
  filters,
  onFiltersChange,
  selectedThreadId,
  onSelectThread,
  showAdminControls,
  socketFactory,
}: ChatWorkspaceProps): React.JSX.Element {
  const threadsQuery = useThreadsQuery(profile, filters);
  // Champ de recherche SIRET local (débounce implicite : appliqué à la soumission du formulaire).
  const [siretDraft, setSiretDraft] = React.useState(filters.q ?? '');

  React.useEffect(() => {
    setSiretDraft(filters.q ?? '');
  }, [filters.q]);

  // Reconstruit un ThreadFilters SANS clé `undefined` (exactOptionalPropertyTypes) : une clé absente
  // vaut « pas de filtre » (URL propre). On repart des valeurs courantes puis on surcharge une clé.
  const buildFilters = (patch: Partial<ThreadFilters>): ThreadFilters => {
    const merged: {
      campaignId: string | undefined;
      unreadOnly: boolean | undefined;
      q: string | undefined;
    } = {
      campaignId: filters.campaignId,
      unreadOnly: filters.unreadOnly,
      q: filters.q,
      ...patch,
    };
    const next: ThreadFilters = {};
    return {
      ...next,
      ...(merged.campaignId !== undefined && merged.campaignId.length > 0
        ? { campaignId: merged.campaignId }
        : {}),
      ...(merged.unreadOnly === true ? { unreadOnly: true } : {}),
      ...(merged.q !== undefined && merged.q.length > 0 ? { q: merged.q } : {}),
    };
  };

  const onSearchSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    onFiltersChange(buildFilters({ q: siretDraft.trim() }));
  };

  const onToggleUnread = (event: React.ChangeEvent<HTMLInputElement>): void => {
    onFiltersChange(buildFilters({ unreadOnly: event.target.checked }));
  };

  const onCampaignChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    onFiltersChange(buildFilters({ campaignId: event.target.value.trim() }));
  };

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="font-serif text-2xl font-semibold tracking-tight">
          {title}
        </h1>
        <p className="text-sm text-muted-foreground">{description}</p>
      </header>

      {showAdminControls ? (
        <Card className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <form
              onSubmit={onSearchSubmit}
              className="flex items-end gap-2"
              role="search"
            >
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="chat-siret-search">Recherche par SIRET</Label>
                <Input
                  id="chat-siret-search"
                  value={siretDraft}
                  onChange={(event) => setSiretDraft(event.target.value)}
                  placeholder="14 chiffres"
                  inputMode="numeric"
                  className="w-48"
                />
              </div>
              <Button type="submit" variant="secondary">
                Rechercher
              </Button>
            </form>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="chat-campaign-filter">Campagne</Label>
              <Input
                id="chat-campaign-filter"
                defaultValue={filters.campaignId ?? ''}
                onBlur={onCampaignChange}
                placeholder="Identifiant de campagne"
                className="w-56"
              />
            </div>

            <label
              htmlFor="chat-unread-only"
              className="flex items-center gap-2 pb-2.5 text-sm text-foreground"
            >
              <input
                id="chat-unread-only"
                type="checkbox"
                checked={filters.unreadOnly === true}
                onChange={onToggleUnread}
                className="h-4 w-4 rounded border-input text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              Non lus uniquement
            </label>
          </div>
        </Card>
      ) : (
        <div className="flex">
          <label
            htmlFor="chat-unread-only"
            className="flex items-center gap-2 text-sm text-foreground"
          >
            <input
              id="chat-unread-only"
              type="checkbox"
              checked={filters.unreadOnly === true}
              onChange={onToggleUnread}
              className="h-4 w-4 rounded border-input text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            Non lus uniquement
          </label>
        </div>
      )}

      <div className="grid min-h-[32rem] grid-cols-1 gap-4 lg:grid-cols-[20rem_1fr]">
        <Card
          aria-label="Liste des conversations"
          className="min-h-0 overflow-y-auto"
        >
          <ChatThreadList
            profile={profile}
            threads={threadsQuery.data?.threads ?? []}
            selectedThreadId={selectedThreadId}
            onSelectThread={onSelectThread}
            isLoading={threadsQuery.isLoading}
          />
        </Card>

        <Card className="flex min-h-0 flex-col">
          {selectedThreadId !== null ? (
            <ChatThreadPanel
              profile={profile}
              threadId={selectedThreadId}
              {...(socketFactory !== undefined ? { socketFactory } : {})}
            />
          ) : (
            <p className="p-6 text-sm text-muted-foreground">
              Sélectionnez une conversation dans la liste pour l'afficher.
            </p>
          )}
        </Card>
      </div>
    </div>
  );
}
